import { useId, useState, type FormEvent } from "react";
import { Link } from "wouter";
import {
  Mail, Phone, MapPin, Send, CheckCircle2, Loader2, AlertCircle, ArrowRight, LogIn,
} from "lucide-react";
import { MarketingLayout } from "../components/MarketingLayout";
import { Reveal } from "../components/Reveal";
import { useSEO } from "../lib/useSEO";
import {
  COMPANY,
  CONTACT_COMPANY_SIZES,
  CONTACT_INTERESTS,
  CONTACT_TRUST_MODULES,
} from "../lib/content";

interface FormState {
  name: string;
  company: string;
  email: string;
  phone: string;
  companySize: string;
  interest: string;
  message: string;
}

type FieldKey = keyof FormState;
type Errors = Partial<Record<FieldKey | "_form", string>>;

const EMPTY: FormState = {
  name: "",
  company: "",
  email: "",
  phone: "",
  companySize: "",
  interest: "",
  message: "",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+\d][\d\s()-]{6,}$/;

function validate(values: FormState): Errors {
  const e: Errors = {};
  const name = values.name.trim();
  if (!name) e.name = "Please enter your name";
  else if (name.length < 2) e.name = "Name is a little short";
  else if (name.length > 100) e.name = "Name must be 100 characters or fewer";

  const company = values.company.trim();
  if (!company) e.company = "Please enter your company";
  else if (company.length < 2) e.company = "Company name is a little short";
  else if (company.length > 100) e.company = "Company must be 100 characters or fewer";

  const email = values.email.trim();
  if (!email) e.email = "Please enter your work email";
  else if (!EMAIL_RE.test(email) || email.length > 254) e.email = "Enter a valid email address";

  if (values.phone.trim() && !PHONE_RE.test(values.phone.trim())) {
    e.phone = "Enter a valid phone number";
  }

  const message = values.message.trim();
  if (!message) e.message = "Please tell us how we can help";
  else if (message.length < 10) e.message = "Message is a little short";
  else if (message.length > 2000) e.message = "Message must be 2000 characters or fewer";

  return e;
}

function applyServerFields(fields: Record<string, string[]> | undefined): Errors {
  if (!fields) return {};
  const out: Errors = {};
  for (const [key, messages] of Object.entries(fields)) {
    if (!messages?.length) continue;
    if (key === "_root" || key === "_form") {
      out._form = messages[0];
      continue;
    }
    if (key in EMPTY) out[key as FieldKey] = messages[0];
  }
  return out;
}

export default function Contact() {
  useSEO({
    title: "Contact Alea ERP | Talk to Our Team",
    description:
      "Contact Alea ERP to book a demo, discuss your requirements, or learn how our ERP platform connects HR, payroll, finance and business operations.",
    path: "/contact",
  });

  const formId = useId();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Errors>({});
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [statusMessage, setStatusMessage] = useState("");

  function update<K extends FieldKey>(key: K, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key] || errors._form) {
      setErrors((e) => {
        const next = { ...e, [key]: undefined };
        delete next._form;
        return next;
      });
    }
  }

  async function onSubmit(ev: FormEvent) {
    ev.preventDefault();
    if (status === "submitting") return;

    const clientErrors = validate(form);
    setErrors(clientErrors);
    if (Object.keys(clientErrors).length > 0) return;

    setStatus("submitting");
    setStatusMessage("");

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          company: form.company.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          companySize: form.companySize || undefined,
          interest: form.interest || undefined,
          message: form.message.trim(),
        }),
      });

      const body = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        error?: string;
        fields?: Record<string, string[]>;
      };

      if (!res.ok) {
        const fieldErrors = applyServerFields(body.fields);
        setErrors({
          ...fieldErrors,
          _form:
            fieldErrors._form ||
            body.error ||
            "We couldn't send your message right now. Please try again or contact us directly.",
        });
        setStatus("error");
        setStatusMessage(
          body.error ||
            "We couldn't send your message right now. Please try again or contact us directly.",
        );
        return;
      }

      setForm(EMPTY);
      setErrors({});
      setStatus("success");
      setStatusMessage("Message sent successfully.");
    } catch {
      setStatus("error");
      setStatusMessage(
        "We couldn't send your message right now. Please try again or contact us directly.",
      );
      setErrors({
        _form:
          "We couldn't send your message right now. Please try again or contact us directly.",
      });
    }
  }

  const fieldClass = (key: FieldKey) =>
    `w-full rounded-lg border bg-white px-4 py-3 text-sm text-slate-900 outline-none transition-colors placeholder:text-slate-400 focus:ring-2 focus:ring-blue-500/30 disabled:opacity-60 ${
      errors[key] ? "border-red-400 focus:border-red-500" : "border-slate-300 focus:border-blue-500"
    }`;

  const telHref = `tel:${COMPANY.phone.replace(/\s/g, "")}`;
  const mailHref = `mailto:${COMPANY.email}`;

  return (
    <MarketingLayout>
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#0a2d4a] pt-32 pb-20 lg:pt-40">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-32 left-1/2 h-96 w-full max-w-[700px] -translate-x-1/2 rounded-full bg-blue-500/20 blur-[120px]"
        />
        <div className="relative mx-auto max-w-3xl px-5 text-center sm:px-8">
          <Reveal>
            <span className="text-sm font-semibold uppercase tracking-wider text-blue-300">
              Contact
            </span>
            <h1 className="mt-3 text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
              Let&apos;s talk about your business
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-slate-300">
              See how Alea ERP can connect your people, payroll, finance and operations
              in one platform. Book a demo, ask a question, or tell us what you&apos;re
              trying to solve. Our team typically replies within one business day.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Trust strip */}
      <section className="border-b border-slate-100 bg-white py-8" aria-label="Product modules">
        <div className="mx-auto max-w-7xl px-5 text-center sm:px-8">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
            Built for modern business operations
          </p>
          <p className="mt-2 text-sm font-medium text-[#0a2d4a]">
            {CONTACT_TRUST_MODULES.join(" · ")}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="bg-white py-16 sm:py-20">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 sm:px-8 lg:grid-cols-[1fr_1.35fr]">
          {/* Contact information */}
          <Reveal className="space-y-5">
            <div className="flex gap-4 rounded-2xl border border-slate-200 bg-slate-50/60 p-5">
              <div className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-600 text-white">
                <Mail aria-hidden="true" className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Email
                </h2>
                <a
                  href={mailHref}
                  className="mt-1 block text-sm font-medium text-[#0a2d4a] hover:text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
                >
                  {COMPANY.email}
                </a>
                <p className="mt-1 text-xs text-slate-500">
                  For business enquiries and general questions.
                </p>
              </div>
            </div>

            <div className="flex gap-4 rounded-2xl border border-slate-200 bg-slate-50/60 p-5">
              <div className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-600 text-white">
                <Phone aria-hidden="true" className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Phone
                </h2>
                <a
                  href={telHref}
                  className="mt-1 block text-sm font-medium text-[#0a2d4a] hover:text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
                >
                  {COMPANY.phone}
                </a>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5">
              <div className="flex gap-4">
                <div className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-600 text-white">
                  <MapPin aria-hidden="true" className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                    Visit our office
                  </h2>
                  <p className="mt-1 text-sm font-bold text-[#0a2d4a]">{COMPANY.name}</p>
                  <p className="mt-1 text-sm leading-relaxed text-slate-600">
                    {COMPANY.address}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">India</p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <h2 className="text-sm font-bold text-[#0a2d4a]">Already using Alea ERP?</h2>
              <p className="mt-1.5 text-sm text-slate-500">
                Log in to access your account, approvals, and in-app helpdesk.
              </p>
              <Link
                href="/sign-in"
                className="mt-4 inline-flex items-center gap-1.5 rounded text-sm font-semibold text-blue-600 hover:text-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
              >
                <LogIn aria-hidden="true" className="h-4 w-4" />
                Log in to your account
                <ArrowRight aria-hidden="true" className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>

          {/* Form */}
          <Reveal delay={0.08}>
            <div className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm sm:p-9">
              {status === "success" ? (
                <div
                  className="flex flex-col items-center justify-center py-12 text-center"
                  role="status"
                  aria-live="polite"
                >
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-green-100 text-green-600">
                    <CheckCircle2 aria-hidden="true" className="h-7 w-7" />
                  </div>
                  <h2 className="mt-5 text-xl font-bold text-[#0a2d4a]">
                    Message sent successfully
                  </h2>
                  <p className="mt-2 max-w-sm text-sm text-slate-600">
                    Our team has received your request and typically replies within one
                    business day. In the meantime, you can explore the live demo.
                  </p>
                  <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                    <Link
                      href="/demo-access"
                      className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white hover:bg-blue-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                    >
                      Explore Demo
                      <ArrowRight aria-hidden="true" className="h-4 w-4" />
                    </Link>
                    <button
                      type="button"
                      onClick={() => {
                        setStatus("idle");
                        setStatusMessage("");
                      }}
                      className="rounded-xl border border-slate-300 bg-white px-6 py-3 text-sm font-semibold text-[#0a2d4a] hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                    >
                      Send another message
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={onSubmit} noValidate className="space-y-5" aria-busy={status === "submitting"}>
                  <div>
                    <h2 className="text-xl font-extrabold tracking-tight text-[#0a2d4a]">
                      Send us a message
                    </h2>
                    <p className="mt-1 text-sm text-slate-500">
                      Tell us about your business — we&apos;ll follow up with next steps.
                    </p>
                  </div>

                  {(errors._form || status === "error") && (
                    <div
                      role="alert"
                      className="flex items-start gap-2.5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                    >
                      <AlertCircle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>
                        {errors._form || statusMessage ||
                          "We couldn't send your message right now. Please try again or contact us directly."}
                        {" "}
                        <a href={mailHref} className="font-semibold underline underline-offset-2">
                          Email {COMPANY.email}
                        </a>
                      </span>
                    </div>
                  )}

                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor={`${formId}-name`} className="mb-1.5 block text-sm font-medium text-slate-700">
                        Name <span className="text-red-500" aria-hidden="true">*</span>
                        <span className="sr-only">(required)</span>
                      </label>
                      <input
                        id={`${formId}-name`}
                        name="name"
                        type="text"
                        autoComplete="name"
                        required
                        aria-required="true"
                        aria-invalid={!!errors.name}
                        aria-describedby={errors.name ? `${formId}-name-err` : undefined}
                        disabled={status === "submitting"}
                        value={form.name}
                        onChange={(e) => update("name", e.target.value)}
                        className={fieldClass("name")}
                        placeholder="Your full name"
                      />
                      {errors.name && (
                        <p id={`${formId}-name-err`} className="mt-1 text-xs text-red-500" role="alert">
                          {errors.name}
                        </p>
                      )}
                    </div>
                    <div>
                      <label htmlFor={`${formId}-company`} className="mb-1.5 block text-sm font-medium text-slate-700">
                        Company <span className="text-red-500" aria-hidden="true">*</span>
                        <span className="sr-only">(required)</span>
                      </label>
                      <input
                        id={`${formId}-company`}
                        name="company"
                        type="text"
                        autoComplete="organization"
                        required
                        aria-required="true"
                        aria-invalid={!!errors.company}
                        aria-describedby={errors.company ? `${formId}-company-err` : undefined}
                        disabled={status === "submitting"}
                        value={form.company}
                        onChange={(e) => update("company", e.target.value)}
                        className={fieldClass("company")}
                        placeholder="Company name"
                      />
                      {errors.company && (
                        <p id={`${formId}-company-err`} className="mt-1 text-xs text-red-500" role="alert">
                          {errors.company}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor={`${formId}-email`} className="mb-1.5 block text-sm font-medium text-slate-700">
                        Work email <span className="text-red-500" aria-hidden="true">*</span>
                        <span className="sr-only">(required)</span>
                      </label>
                      <input
                        id={`${formId}-email`}
                        name="email"
                        type="email"
                        autoComplete="email"
                        required
                        aria-required="true"
                        aria-invalid={!!errors.email}
                        aria-describedby={errors.email ? `${formId}-email-err` : undefined}
                        disabled={status === "submitting"}
                        value={form.email}
                        onChange={(e) => update("email", e.target.value)}
                        className={fieldClass("email")}
                        placeholder="you@company.com"
                      />
                      {errors.email && (
                        <p id={`${formId}-email-err`} className="mt-1 text-xs text-red-500" role="alert">
                          {errors.email}
                        </p>
                      )}
                    </div>
                    <div>
                      <label htmlFor={`${formId}-phone`} className="mb-1.5 block text-sm font-medium text-slate-700">
                        Phone <span className="text-slate-400">(optional)</span>
                      </label>
                      <input
                        id={`${formId}-phone`}
                        name="phone"
                        type="tel"
                        autoComplete="tel"
                        aria-invalid={!!errors.phone}
                        aria-describedby={errors.phone ? `${formId}-phone-err` : undefined}
                        disabled={status === "submitting"}
                        value={form.phone}
                        onChange={(e) => update("phone", e.target.value)}
                        className={fieldClass("phone")}
                        placeholder="+91 98765 43210"
                      />
                      {errors.phone && (
                        <p id={`${formId}-phone-err`} className="mt-1 text-xs text-red-500" role="alert">
                          {errors.phone}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor={`${formId}-size`} className="mb-1.5 block text-sm font-medium text-slate-700">
                        Company size <span className="text-slate-400">(optional)</span>
                      </label>
                      <select
                        id={`${formId}-size`}
                        name="companySize"
                        disabled={status === "submitting"}
                        value={form.companySize}
                        onChange={(e) => update("companySize", e.target.value)}
                        className={fieldClass("companySize")}
                      >
                        <option value="">Select a range</option>
                        {CONTACT_COMPANY_SIZES.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label} users
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label htmlFor={`${formId}-interest`} className="mb-1.5 block text-sm font-medium text-slate-700">
                        What are you interested in? <span className="text-slate-400">(optional)</span>
                      </label>
                      <select
                        id={`${formId}-interest`}
                        name="interest"
                        disabled={status === "submitting"}
                        value={form.interest}
                        onChange={(e) => update("interest", e.target.value)}
                        className={fieldClass("interest")}
                      >
                        <option value="">Select a focus area</option>
                        {CONTACT_INTERESTS.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor={`${formId}-message`} className="mb-1.5 block text-sm font-medium text-slate-700">
                      Message <span className="text-red-500" aria-hidden="true">*</span>
                      <span className="sr-only">(required)</span>
                    </label>
                    <textarea
                      id={`${formId}-message`}
                      name="message"
                      rows={5}
                      required
                      aria-required="true"
                      aria-invalid={!!errors.message}
                      aria-describedby={errors.message ? `${formId}-message-err` : undefined}
                      disabled={status === "submitting"}
                      value={form.message}
                      onChange={(e) => update("message", e.target.value)}
                      className={`${fieldClass("message")} resize-none`}
                      placeholder="Tell us what you're trying to solve…"
                    />
                    {errors.message && (
                      <p id={`${formId}-message-err`} className="mt-1 text-xs text-red-500" role="alert">
                        {errors.message}
                      </p>
                    )}
                  </div>

                  <p className="text-xs text-slate-500">
                    By submitting this form, you agree to our{" "}
                    <Link
                      href="/privacy"
                      className="font-semibold text-blue-600 hover:text-blue-700 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
                    >
                      Privacy Policy
                    </Link>
                    .
                  </p>

                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="group inline-flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-7 py-3.5 text-sm font-semibold text-white shadow-lg transition-all hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 sm:w-auto"
                  >
                    {status === "submitting" ? (
                      <>
                        <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
                        Sending…
                      </>
                    ) : (
                      <>
                        Send message
                        <Send aria-hidden="true" className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* What happens next */}
      <section className="bg-slate-50 py-14 sm:py-16" aria-labelledby="next-steps-heading">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="text-center">
            <h2 id="next-steps-heading" className="text-2xl font-extrabold tracking-tight text-[#0a2d4a]">
              What happens next?
            </h2>
          </Reveal>
          <div className="mt-8 grid gap-4 sm:grid-cols-3">
            {[
              {
                step: "1",
                title: "We review your request",
                text: "Our team reviews your requirements and enquiry.",
              },
              {
                step: "2",
                title: "We get in touch",
                text: "We contact you to understand your business and requirements.",
              },
              {
                step: "3",
                title: "See Alea ERP in action",
                text: "We walk you through the relevant modules and workflows.",
              },
            ].map((item, i) => (
              <Reveal key={item.step} delay={i * 0.06}>
                <div className="h-full rounded-2xl border border-slate-200 bg-white p-5">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-blue-50 text-sm font-bold text-blue-700">
                    {item.step}
                  </span>
                  <h3 className="mt-3 text-sm font-bold text-[#0a2d4a]">{item.title}</h3>
                  <p className="mt-1.5 text-sm text-slate-500">{item.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
